import React, { useEffect } from "react";
import { trackAnalyticsActionClicked } from "utils/Tracking";
import { formatDateTime } from "utils/analytics/dateUtils";
import { ErrorLogRow } from "utils/analytics/types";
import { useLocation, useNavigate } from "react-router-dom";
import SeverityBadge from "../shared/components/SeverityBadge";
import ErrorLogDetailsPanel from "./ErrorLogDetailsPanel";

export const renderErrorCell = (item: ErrorLogRow, key: string): React.ReactNode => {
  const value = (item as Record<string, any>)[key];

  switch (key) {
    case "timestamp":
      return <span className="text-sm">{formatDateTime(value)}</span>;
    case "severity":
      return <SeverityBadge severity={item.severity ?? "medium"} />;
    case "status_code":
      return <span className="font-medium">{value || "N/A"}</span>;
    case "request_id":
      return <span className="font-mono text-xs">{value || "N/A"}</span>;
    case "url":
      // max-w bounds the cell so the ellipsis can truncate — without it long URLs stretch the table
      return (
        <div
          className="overflow-hidden text-ellipsis whitespace-nowrap max-w-[300px]"
          title={value}
        >
          {value || "N/A"}
        </div>
      );
    case "retries":
      return value || "0";
    default:
      return value ?? "N/A";
  }
};

/** Mounted only while its row is expanded, so the open-tracking fires on mount. */
export const ErrorLogExpandedRow = ({ item }: { item: ErrorLogRow }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const itemId = item.request_id;
  const requestText = item.request_text?.trim() || "No request data available";

  useEffect(() => {
    trackAnalyticsActionClicked({
      section: "global_all",
      action: "view_error_details",
      target: "error_log_disclosure",
    });
  }, []);

  const handleOpenCodeModal = (e: React.MouseEvent, requestContent: string) => {
    e.stopPropagation();
    navigate("/analytics/errored-request", {
      state: {
        backgroundLocation: location,
        requestContent: requestContent,
        requestId: itemId,
      },
    });
  };

  return (
    <div className="bg-[#ecf0f3] border-t">
      <ErrorLogDetailsPanel
        url={item.url}
        statusMessage={item.status_message}
        requestText={requestText}
        onOpenCodeModal={handleOpenCodeModal}
      />
    </div>
  );
};

